import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ToastContainer } from 'react-toastify';
import {
    Search,
    Filter,
    ChevronUp,
    ChevronDown,
    X,
    Sparkles,
    FlaskConical,
    ClipboardList,
    Calendar,
    Users,
    Building2,
    FileText,
    RefreshCw,
    MessageSquare,
    ArrowUpDown,
} from 'lucide-react';

import { BASE_URL } from '../../config/axios';
import { handleErrorToast } from '../../components/CustomFunctions';
import './css/viewproject.css'
import AddProjectDetails from './AddProjectDetails';
import { DeleteProjectDetails, UpdateProjectDetails } from './CrudPRojectDetails';
import { UploadProjectDetails } from './BulkCrud';
import { DocumentModel } from '../../components/DocumentModel';
const formatDateLong = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const statusClassMap = {
    Open: 'submitted',
    Ongoing: 'recommended',
    Closed: 'approved',
};

const ViewProject = () => {
    const [projects, setProjects] = useState([]);
    const [sortOrder, setSortOrder] = useState('desc');
    const [buffer, setBuffering] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedSearch, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('null');
    const [showFilters, setShowFilters] = useState(true);
    const [expandedRow, setExpandedRow] = useState(null);

    const getProjects = async () => {
        setBuffering(true);
        try {
            const res = await axios.get(`${BASE_URL}/labmonitoring/labjob/`);
            const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
            setProjects(data);
        } catch (err) {
            handleErrorToast(err);
        } finally {
            setBuffering(false);
        }
    };

    useEffect(() => {
        getProjects();
    }, []);

    const applyFilters = () => {
        let data = [...projects]; // copy so the original state isn't mutated

        if (selectedSearch !== '') {
            const q = selectedSearch.toLowerCase();
            data = data.filter((project) => {
                const code = project.project_code?.toString().toLowerCase().includes(q);
                const name = project.project_name?.toString().toLowerCase().includes(q);
                const client = project.client_detail?.toString().toLowerCase().includes(q);
                const person = project.responsible_person?.toString().toLowerCase().includes(q);
                return code || name || client || person;
            });
        }

        if (statusFilter !== 'null') {
            data = data.filter((project) => project.project_status === statusFilter);
        }

        // Sort by project_confirmation_date (empty dates always last)
        data.sort((a, b) => {
            const da = a.project_confirmation_date ? new Date(a.project_confirmation_date).getTime() : null;
            const db = b.project_confirmation_date ? new Date(b.project_confirmation_date).getTime() : null;

            if (da === null && db === null) return 0;
            if (da === null) return 1;
            if (db === null) return -1;

            return sortOrder === 'asc' ? da - db : db - da;
        });

        setFilteredData(data);
    };

    useEffect(() => {
        applyFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSearch, projects, statusFilter, sortOrder]); // added sortOrder

    useEffect(() => {
        applyFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSearch, projects, statusFilter]);

    const toggleRow = (index) => {
        setExpandedRow(expandedRow === index ? null : index);
    };

    const hasActiveFilters = selectedSearch !== '' || statusFilter !== 'null';

    const clearAllFilters = () => {
        setSearch('');
        setStatusFilter('null');
    };

    return (
        <div className="progress-modern-container">

            {showFilters && (
                <div className="filter-bar-attractive " style={{ display: 'flex', justifyContent: "space-between" }}>
                    <div className="filter-group-attractive">
                        <div className="filter-chip" style={{ minWidth: '260px' }}>
                            <div><Search size={15} /></div>
                            <div className="filter-chip-content" style={{ width: '100%' }}>
                                <input
                                    className="modern-select"
                                    style={{ width: '100%' }}
                                    placeholder="Project Code / Name / Client / Responsible"
                                    value={selectedSearch}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className={`filter-chip ${statusFilter !== 'null' ? 'active' : ''}`}>
                            <div className="filter-chip-content">
                                <select
                                    className="modern-select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="null">All Status</option>
                                    <option value="Open">Open</option>
                                    <option value="Ongoing">Ongoing</option>
                                    <option value="Closed">Closed</option>
                                </select>
                            </div>
                        </div>


                        {hasActiveFilters && (
                            <button className="clear-filters-btn" onClick={clearAllFilters}>
                                <X size={13} />
                                Clear
                            </button>
                        )}
                    </div>
                    <div>
                        <UploadProjectDetails getProjects={getProjects} />
                        <AddProjectDetails getProjects={getProjects} />
                    </div>
                </div>
            )}

            {/* Stats + toggle + actions */}
            <div style={{ display: 'flex', gap: '10px', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="stats-minimal-row">
                    <div className="stat-minimal-item">
                        <FlaskConical size={14} />
                        <span>Showing</span>
                        <strong>{filteredData.length} projects</strong>
                    </div>
                </div>

                <div className="filter-bar-top" style={{ width: 'auto', gap: '10px' }}>
                    <button className="toggle-filters-btn" onClick={() => setShowFilters(!showFilters)}>
                        <Filter size={14} />
                        <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
                        {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                    <button
                        className="toggle-filters-btn"
                        onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                    >
                        <ArrowUpDown size={14} />
                        <span>Confirmation Date: {sortOrder === 'desc' ? 'Newest' : 'Oldest'}</span>
                    </button>
                    <button className="toggle-filters-btn" onClick={getProjects}>
                        <RefreshCw size={14} className={buffer ? 'spinning' : ''} />
                        <span>Refresh</span>
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="table-wrapper-minimal table-box scroll-container-table">
                {buffer ? (
                    <div className="loader-minimal">
                        <div className="spinner-minimal"></div>
                    </div>
                ) : (
                    <table className="progress-table-minimal">
                        <thead className="table-heading" style={{ zIndex: 1000 }}>
                            <tr>
                                <th className="col-sno" style={{ width: '5%' }}>#</th>
                                <th className="col-period text-start">Project Details</th>
                                <th className="col-period text-start">Documents</th>
                                <th className="col-submitted text-center" style={{ width: '14%' }}>Client</th>
                                <th className="col-approved text-center" style={{ width: '14%' }}>Responsible</th>
                                <th className="col-status text-center" style={{ width: '12%' }}>Status</th>
                                <th className="col-action text-center" style={{ width: '14%' }}>Confirmation</th>
                                <th className="col-action text-center" style={{ width: '14%' }}>Action </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map((project, index) => {
                                const isExpanded = expandedRow === index;
                                return (
                                    <React.Fragment key={project.id ?? index}>
                                        <tr className="progress-row">
                                            <td className="col-sno text-start">{index + 1}.</td>
                                            <td className="col-period">
                                                <div className="flex-row justify-center" style={{ justifyContent: 'center', alignItems: 'center' }}>
                                                    <button className="period-trigger" style={{ width: '60%', textAlign: 'left', justifyContent: 'center' }} onClick={() => toggleRow(index)}>
                                                        <FlaskConical size={12} />
                                                        <span>{project?.project_code} • {project?.project_name}</span>
                                                        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="col-submitted text-center">
                                                <DocumentModel project={project.id} title="Project Documents" getDocument={getProjects} />
                                            </td>
                                            <td className="col-submitted text-center">
                                                <span className="badge-value">{project?.client_detail || '-'}</span>
                                            </td>
                                            <td className="col-approved text-center">
                                                <span className="badge-value">{project?.responsible_person || '-'}</span>
                                            </td>
                                            <td className="col-status text-center">
                                                <span className={`status-pill status-pill-${statusClassMap[project?.project_status] || 'submitted'}`}>
                                                    {project?.project_status || '-'}
                                                </span>
                                            </td>
                                            <td className="col-action text-center">
                                                <span className="badge-value">{formatDateLong(project?.project_confirmation_date)}</span>
                                            </td>
                                            <td className="col-action text-center" >
                                                <div style={{ display: "flex", justifyContent: 'center', gap: '2px' }}>
                                                    <UpdateProjectDetails i={project} getProjects={getProjects} />
                                                    <DeleteProjectDetails i={project} getData={getProjects} />
                                                </div>
                                            </td>
                                        </tr>

                                        {isExpanded && (
                                            <tr className="expanded-row">
                                                <td colSpan={7}>
                                                    <div className="expanded-content-attractive">
                                                        <div className="expanded-content-header">
                                                            <div className="expanded-content-title">
                                                                <Sparkles size={13} />
                                                                <span>Full Project Details</span>
                                                            </div>
                                                        </div>

                                                        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                                                            {/* ===== BASIC INFO ===== */}
                                                            <div className="stage-card submitted" style={{ flex: 1, minWidth: '240px' }}>
                                                                <div className="stage-header">
                                                                    <Building2 size={14} />
                                                                    <span>Basic Info</span>
                                                                </div>
                                                                <div className="stage-content">
                                                                    <div className="stage-row"><span className="stage-label">Project Code</span><span className="amount">{project?.project_code || '-'}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Project Name</span><span className="amount">{project?.project_name || '-'}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Project Scope</span><span className="amount">{project?.project_scope || '-'}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Client Detail</span><span className="amount">{project?.client_detail || '-'}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Client Concern Person</span><span className="amount">{project?.client_concern_person || '-'}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Status</span><span className="amount">{project?.project_status || '-'}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Project Description</span><span className="amount">{project?.project_description || '-'}</span></div>
                                                                </div>
                                                            </div>

                                                            {/* ===== TESTING DETAILS ===== */}
                                                            <div className="stage-card recommended" style={{ flex: 1, minWidth: '240px' }}>
                                                                <div className="stage-header">
                                                                    <FlaskConical size={14} />
                                                                    <span>Testing Details</span>
                                                                </div>
                                                                <div className="stage-content">
                                                                    <div className="stage-row"><span className="stage-label">Test Performed</span><span className="amount">{project?.test_performed || '-'}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Sample Quantity</span><span className="amount">{project?.lab_sample_quantity ?? '-'}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Dept / Equipment</span><span className="amount">{project?.department_equipment || '-'}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Responsible Person</span><span className="amount">{project?.responsible_person || '-'}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Testing Person(s)</span><span className="amount">{project?.testing_person || '-'}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Test Planned</span><span className="amount">{project?.test_planned_status ? 'Yes' : 'No'}</span></div>
                                                                    {project?.test_planned_status && (
                                                                        <div className="stage-row"><span className="stage-label">Test Planned Date</span><span className="amount">{formatDateLong(project?.test_planned_date)}</span></div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* ===== TIMELINE ===== */}
                                                            <div className="stage-card approved" style={{ flex: 1, minWidth: '240px' }}>
                                                                <div className="stage-header">
                                                                    <Calendar size={14} />
                                                                    <span>Timeline</span>
                                                                </div>
                                                                <div className="stage-content">
                                                                    <div className="stage-row"><span className="stage-label">Confirmation Date</span><span className="amount">{formatDateLong(project?.project_confirmation_date)}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Mobilization Date</span><span className="amount">{formatDateLong(project?.mobilization_date)}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Survey Date</span><span className="amount">{formatDateLong(project?.survey_date)}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Completion Date</span><span className="amount">{formatDateLong(project?.completion_date)}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Sample Received (HO)</span><span className="amount">{formatDateLong(project?.sample_receiving_date_ho)}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Data Received (Client)</span><span className="amount">{formatDateLong(project?.project_data_received_from_client)}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Total Duration</span><span className="amount">{project?.total_duration ? `${project.total_duration} days` : '-'}</span></div>
                                                                </div>
                                                            </div>

                                                            {/* ===== REPORT & REMARKS ===== */}
                                                            <div className="stage-card submitted" style={{ flex: 1, minWidth: '240px' }}>
                                                                <div className="stage-header">
                                                                    <ClipboardList size={14} />
                                                                    <span>Report & Remarks</span>
                                                                </div>
                                                                <div className="stage-content">
                                                                    <div className="stage-row"><span className="stage-label">Draft Report Date</span><span className="amount">{formatDateLong(project?.draft_report_submission_date)}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Final Report Date</span><span className="amount">{formatDateLong(project?.final_report_submission_date)}</span></div>
                                                                    <div className="stage-row"><span className="stage-label">Report Revised</span><span className="amount">{project?.report_revised_status ? 'Yes' : 'No'}</span></div>
                                                                    {project?.report_revised_status && (
                                                                        <>
                                                                            <div className="stage-row"><span className="stage-label">Revised Date</span><span className="amount">{formatDateLong(project?.revised_date)}</span></div>
                                                                            <div className="stage-row"><span className="stage-label">Revised Reason</span><span className="amount">{project?.revised_reason || '-'}</span></div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* ===== REMARK 1 & 2 ===== */}
                                                        {(project?.remark_1 || project?.remark_2) && (
                                                            <div style={{ marginTop: '12px', padding: '10px 14px', background: '#f8fafc', borderRadius: '10px', fontSize: '12px', color: '#475569' }}>
                                                                {project?.remark_1 && <div><strong style={{ color: '#1e293b' }}>Remark 1: </strong>{project.remark_1}</div>}
                                                                {project?.remark_2 && <div style={{ marginTop: '6px' }}><strong style={{ color: '#1e293b' }}>Remark 2: </strong>{project.remark_2}</div>}
                                                            </div>
                                                        )}

                                                        {/* ===== ADDITIONAL REMARKS (remarks array) ===== */}
                                                        {Array.isArray(project?.remarks) && project.remarks.length > 0 && (
                                                            <div style={{ marginTop: '12px', padding: '12px 14px', background: '#f0fdf4', borderRadius: '10px', border: '1px solid #bbf7d0' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', color: '#166534', fontWeight: 600, fontSize: '13px' }}>
                                                                    <MessageSquare size={14} />
                                                                    <span>Additional Remarks ({project.remarks.length})</span>
                                                                </div>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                                    {project.remarks.map((r, idx) => (
                                                                        <div
                                                                            key={r.id ?? idx}
                                                                            style={{
                                                                                display: 'flex',
                                                                                gap: '10px',
                                                                                alignItems: 'flex-start',
                                                                                background: '#ffffff',
                                                                                padding: '8px 10px',
                                                                                borderRadius: '8px',
                                                                                border: '1px solid #d1fae5',
                                                                                fontSize: '12px',
                                                                            }}
                                                                        >
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#059669', fontWeight: 600, minWidth: '100px', flexShrink: 0 }}>
                                                                                <Calendar size={12} />
                                                                                <span>{formatDateLong(r.date)}</span>
                                                                            </div>
                                                                            <div style={{ color: '#334155', flex: 1 }}>
                                                                                {r.remarks}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {filteredData.length === 0 && (
                                <tr>
                                    <td colSpan={7}>
                                        <div className="empty-state-attractive">
                                            <div className="empty-state-icon-wrap">
                                                <FlaskConical size={32} />
                                            </div>
                                            <h4 className="empty-state-title">No Projects Found</h4>
                                            <p className="empty-state-subtitle">
                                                Try adjusting your filters or search to find what you're looking for.
                                            </p>

                                            {hasActiveFilters && (
                                                <button className="empty-state-clear-btn" onClick={clearAllFilters}>
                                                    <X size={13} />
                                                    Clear Filters
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
        </div>
    );
};

export default ViewProject;